import { useEffect } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type LocalVideoTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteVideoTrack,
} from 'livekit-client'

import { canLogDiagnostics, getDiagnosticsBudgetUsage } from '@/src/lib/diagnosticsBudget'
import { clientLogStats, clientLogWarn } from '@/src/lib/clientLog'
import {
  collectReceiverSnapshot,
  collectSenderSnapshot,
  collectTransportSnapshots,
} from '@/src/lib/webrtcStats'

/** Сколько треков одновременно держим под наблюдением. */
const MAX_WATCHED_TRACKS = 6

/** Период опроса после стартового всплеска. */
const STEADY_INTERVAL_MS = 30_000

/** Моменты стартовых снимков у получателя: ловим отказ декодера за несколько секунд. */
const RECEIVER_BURST_MS = [1000, 2000, 4000]

/** У отправителя хватает одного снимка: кодек и профиль известны сразу. */
const SENDER_BURST_MS = [3000]

type SamplePhase = 'start' | 'steady' | 'final'

/** Результат одного снимка: что отправить и чем отличить его от предыдущего. */
type SampleResult = {
  snapshot: Record<string, unknown> | null
  signature: string | null
}

type Watch = {
  message: string
  sample: (phase: SamplePhase) => Promise<SampleResult>
  timers: Array<ReturnType<typeof setTimeout>>
  steadyTimer: ReturnType<typeof setTimeout> | null
  lastSignature: string | null
  closed: boolean
}

export type UseScreenShareTelemetryOptions = {
  room?: Room
  enabled?: boolean
}

/**
 * Отличает удалённый видео-трек от локального.
 *
 * `publication.videoTrack` отдаёт объединение двух типов, а для статистики
 * получателя нужен именно удалённый: только у него есть `receiver`.
 *
 * @param track - видео-трек из публикации
 */
function isRemoteVideoTrack(
  track: LocalVideoTrack | RemoteVideoTrack | undefined,
): track is RemoteVideoTrack {
  return Boolean(track && 'receiver' in track)
}

/**
 * Достаёт sid комнаты из внутренностей SDK.
 *
 * Сам SDK кладёт в контекст своих логов пару `room` (имя комнаты, у нас это
 * номер сделки) и `roomID` (sid на стороне сервера). Без второго ключа наши
 * снимки не находятся теми же запросами, что и остальные клиентские логи.
 *
 * @param room - текущая комната livekit
 */
function readRoomSid(room: Room): string | undefined {
  // SAFETY: roomInfo закрыт в публичных типах, но SDK сам читает из него sid
  // для своих логов. Если поля нет или оно другого типа — просто не пишем ключ
  const roomInfo = (room as unknown as { roomInfo?: { sid?: unknown } }).roomInfo
  const sid = roomInfo?.sid

  return typeof sid === 'string' && sid !== '' ? sid : undefined
}

/**
 * Логирует WebRTC-статистику демонстрации экрана в клиентские логи.
 *
 * Хук закрывает слепое место разборов: в логах было не отличить «поток не
 * дошёл» от «поток дошёл, но не декодируется». Теперь оба случая видны по паре
 * `framesReceived` и `framesDecoded` в записи `screen-share-inbound`.
 *
 * Поведение встречи хук не меняет: он только читает статистику и пишет снимки
 * с ограничением частоты.
 *
 * @param options.room - комната livekit, пока undefined хук неактивен
 * @param options.enabled - выключатель диагностики, по умолчанию включена
 */
export function useScreenShareTelemetry({ room, enabled = true }: UseScreenShareTelemetryOptions): void {
  useEffect(() => {
    if (!enabled || !room) {
      return
    }

    // Общий лимит модуля не сбрасываем: сброс на монтировании позволял бы
    // двум экземплярам хука (или StrictMode в dev) обойти ограничение
    const watches = new Map<string, Watch>()
    let transportTimer: ReturnType<typeof setTimeout> | null = null
    let budgetWarningSent = false
    let disposed = false

    /**
     * Собирает ключи, по которым снимок находят в Loki: те же, что у SDK.
     */
    const baseContext = (): Record<string, unknown> => ({
      room: room.name,
      roomID: readRoomSid(room),
      participant: room.localParticipant?.identity,
    })

    /**
     * Отправляет снимок, если он влезает в бюджет и вкладка на виду.
     */
    const emit = (message: string, snapshot: Record<string, unknown>): void => {
      if (disposed || document.visibilityState === 'hidden') {
        return
      }

      if (!canLogDiagnostics()) {
        if (!budgetWarningSent) {
          budgetWarningSent = true
          clientLogWarn('diagnostics-budget-exceeded', {
            ...baseContext(),
            used: getDiagnosticsBudgetUsage(),
          })
        }

        return
      }

      clientLogStats(message, { ...baseContext(), ...snapshot })
    }

    /**
     * Делает снимок и отбрасывает повторы, чтобы опрос раз в 30 секунд не
     * заполнял Loki одинаковыми строками.
     */
    const runSample = async (watch: Watch, phase: SamplePhase): Promise<void> => {
      if (disposed || (watch.closed && phase !== 'final')) {
        return
      }

      try {
        const result = await watch.sample(phase)

        if (!result.snapshot || disposed) {
          return
        }

        const isRepeat =
          phase === 'steady' && result.signature !== null && result.signature === watch.lastSignature

        if (isRepeat) {
          return
        }

        watch.lastSignature = result.signature

        if (phase === 'final') {
          const direction = watch.message === 'screen-share-inbound' ? 'receiver' : 'sender'

          emit('screen-share-final', { ...result.snapshot, direction, phase })

          return
        }

        emit(watch.message, { ...result.snapshot, phase })
      } catch {
        // Снимок не собрался: трек могли закрыть между таймером и опросом
      }
    }

    /**
     * Ставит повторяющийся опрос трека.
     */
    const scheduleSteady = (watch: Watch): void => {
      watch.steadyTimer = setTimeout(() => {
        void runSample(watch, 'steady').then(() => {
          if (!watch.closed && !disposed) {
            scheduleSteady(watch)
          }
        })
      }, STEADY_INTERVAL_MS)
    }

    /**
     * Начинает наблюдение за треком: стартовый всплеск и затем опрос.
     */
    const startWatch = (key: string, watch: Omit<Watch, 'timers' | 'steadyTimer' | 'lastSignature' | 'closed'>): void => {
      if (watches.has(key) || watches.size >= MAX_WATCHED_TRACKS) {
        return
      }

      const entry: Watch = {
        ...watch,
        timers: [],
        steadyTimer: null,
        lastSignature: null,
        closed: false,
      }

      watches.set(key, entry)

      const burst = watch.message === 'screen-share-inbound' ? RECEIVER_BURST_MS : SENDER_BURST_MS

      burst.forEach((delay) => {
        entry.timers.push(
          setTimeout(() => {
            void runSample(entry, 'start')
          }, delay),
        )
      })

      scheduleSteady(entry)
    }

    /**
     * Останавливает наблюдение: финальный снимок и очистка таймеров.
     */
    const stopWatch = (key: string): void => {
      const watch = watches.get(key)

      if (!watch) {
        return
      }

      watch.closed = true
      watches.delete(key)

      watch.timers.forEach((timer) => clearTimeout(timer))
      watch.timers = []

      if (watch.steadyTimer !== null) {
        clearTimeout(watch.steadyTimer)
        watch.steadyTimer = null
      }

      void runSample(watch, 'final')

      if (watches.size === 0 && transportTimer !== null) {
        clearTimeout(transportTimer)
        transportTimer = null
      }
    }

    /**
     * Опрашивает транспорт, пока в комнате есть хотя бы одна демонстрация.
     */
    const scheduleTransport = (): void => {
      if (transportTimer !== null) {
        return
      }

      const tick = async (): Promise<void> => {
        transportTimer = null

        const snapshots = await collectTransportSnapshots(room)

        snapshots.forEach((snapshot) => {
          emit('screen-share-transport', { ...snapshot })
        })

        if (!disposed && watches.size > 0) {
          transportTimer = setTimeout(() => {
            void tick()
          }, STEADY_INTERVAL_MS)
        }
      }

      transportTimer = setTimeout(() => {
        void tick()
      }, STEADY_INTERVAL_MS)
    }

    const watchLocalScreenShare = (publication: LocalTrackPublication): void => {
      const track = publication.videoTrack

      if (!track) {
        return
      }

      startWatch(`sender:${publication.trackSid}`, {
        message: 'screen-share-sender',
        sample: async () => {
          const snapshot = await collectSenderSnapshot(track, publication.trackSid)

          if (!snapshot) {
            return { snapshot: null, signature: null }
          }

          return {
            snapshot: { ...snapshot },
            signature: `${snapshot.mimeType}|${snapshot.framesEncoded}|${snapshot.qualityLimitationReason}`,
          }
        },
      })

      scheduleTransport()
    }

    const watchRemoteScreenShare = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      publisherIdentity: string,
    ): void => {
      startWatch(`receiver:${publication.trackSid}`, {
        message: 'screen-share-inbound',
        sample: async () => {
          const snapshot = await collectReceiverSnapshot(track, publisherIdentity, publication.trackSid)

          if (!snapshot) {
            return { snapshot: null, signature: null }
          }

          return {
            snapshot: { ...snapshot },
            signature: `${snapshot.framesDecoded}|${snapshot.framesReceived}|${snapshot.packetsLost}|${snapshot.framesDropped}|${snapshot.inboundRtpAvailable}|${snapshot.packetsReceivedAvailable}|${snapshot.framesReceivedAvailable}|${snapshot.framesDecodedAvailable}`,
          }
        },
      })

      scheduleTransport()
    }

    const handleLocalTrackPublished = (publication: LocalTrackPublication): void => {
      if (publication.source !== Track.Source.ScreenShare) {
        return
      }

      watchLocalScreenShare(publication)
    }

    const handleLocalTrackUnpublished = (publication: LocalTrackPublication): void => {
      if (publication.source !== Track.Source.ScreenShare) {
        return
      }

      stopWatch(`sender:${publication.trackSid}`)
    }

    const handleTrackSubscribed = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare || track.kind !== Track.Kind.Video) {
        return
      }

      watchRemoteScreenShare(track, publication, participant.identity)
    }

    const handleTrackUnsubscribed = (
      _track: RemoteTrack,
      publication: RemoteTrackPublication,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare) {
        return
      }

      stopWatch(`receiver:${publication.trackSid}`)
    }

    // Хук может смонтироваться позже публикации: например, после reconnect
    // LiveKitRoom пересоздаёт комнату, и события уже отгремели
    const localScreenShare = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)

    if (localScreenShare) {
      watchLocalScreenShare(localScreenShare)
    }

    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.source !== Track.Source.ScreenShare) {
          return
        }

        if (publication.isSubscribed && isRemoteVideoTrack(publication.videoTrack)) {
          watchRemoteScreenShare(publication.videoTrack, publication, participant.identity)
        }
      })
    })

    room
      .on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)
      .on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished)
      .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
      .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)

    // В скрытой вкладке снимки не шлём, но после возврата даём один свежий:
    // именно на паузе вкладки у людей и рассыпается демонстрация
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }

      watches.forEach((watch) => {
        void runSample(watch, 'steady')
      })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      disposed = true

      document.removeEventListener('visibilitychange', handleVisibilityChange)

      room
        .off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)
        .off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished)
        .off(RoomEvent.TrackSubscribed, handleTrackSubscribed)
        .off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)

      watches.forEach((watch) => {
        watch.closed = true
        watch.timers.forEach((timer) => clearTimeout(timer))
        watch.timers = []

        if (watch.steadyTimer !== null) {
          clearTimeout(watch.steadyTimer)
          watch.steadyTimer = null
        }
      })

      watches.clear()

      if (transportTimer !== null) {
        clearTimeout(transportTimer)
        transportTimer = null
      }
    }
  }, [room, enabled])
}
